'use client';
import React, { FC, useEffect, useState } from 'react';
import {
  TypeInputDropDownFields,
  TypeKeyValuePairFields,
  TypeLink_external,
  TypeLink_externalFields,
  TypeLink_internal,
  TypeLink_internalFields,
} from '@/core/generated-types';
import { InternalLink } from '../InternalLink';
import { ExternalLink } from '../ExternalLink';
import { LuvImage } from '@/components';
import { Entry } from 'contentful';
import classNames from 'classnames';
import useLocale from '@/hooks/useLocale';
import { usePathname } from '@/navigation';
import { ChevronIcon } from '../Icons';
import { useHandleOutsideClick } from '@/utils/handleOutsideClick';

enum ContentfulDropdownIds {
  region = 'region',
  language = 'language',
}

interface InputOptionProps {
  option?: Entry<
    TypeLink_externalFields | TypeKeyValuePairFields | TypeLink_internalFields
  >;
  label?: string;
  className?: string;
}

const InputOption: FC<InputOptionProps> = ({ option, label, className }) => {
  if (!option) {
    return (
      <div
        className={classNames(className, 'flex h-12 items-center px-2 text-sm')}
      >
        {label}
      </div>
    );
  }

  switch (option.sys.contentType.sys.id) {
    case 'link_internal':
      return (
        <div
          className={classNames(
            className,
            'flex h-12 w-full items-center px-2 text-sm',
          )}
          key={option.sys.id}
        >
          <InternalLink
            key={option.sys.id}
            link={option as TypeLink_internal}
            classes="h-full w-full"
          />
        </div>
      );

    case 'link_external':
      const { fields } = option as TypeLink_external;
      const { linkImage } = fields;
      return (
        <div
          className={classNames(
            className,
            'flex h-12 w-full items-center px-2 text-sm',
          )}
          key={option.sys.id}
        >
          <ExternalLink
            key={option.sys.id}
            link={option as TypeLink_external}
            classes="h-full w-full"
          >
            {linkImage && (
              <figure className="inline-block">
                <LuvImage imageData={linkImage} width={27} height={27} />
              </figure>
            )}
            {fields.linkText}
          </ExternalLink>
        </div>
      );

    case 'keyValuePair':
      const { title } = option?.fields as TypeKeyValuePairFields;
      return (
        <div
          className={classNames(
            className,
            'flex h-12 items-center p-2 text-sm',
          )}
          key={option.sys.id}
        >
          <p>{title}</p>
        </div>
      );
  }

  return null;
};

const InputDropDown: FC<TypeInputDropDownFields> = ({
  id,
  label,
  dropdownOptions,
  boldTitle,
}) => {
  const locale = useLocale();
  const pathname = usePathname();
  const [value, setValue] = useState<any>();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useHandleOutsideClick(() => setIsOpen(false));

  // Set default values per ID
  useEffect(() => {
    switch (id) {
      case ContentfulDropdownIds.region:
        const tld = `.${process.env.NEXT_PUBLIC_REGION}`.toLowerCase();
        const regionOption =
          dropdownOptions.find((option: any) => {
            return option.fields.linkUrl.includes(tld);
          }) || dropdownOptions[0];

        setValue(regionOption);
        break;
      case ContentfulDropdownIds.language:
        const languageOption =
          dropdownOptions.find((option: any) => {
            return option.fields.linkUrl.includes(locale);
          }) || dropdownOptions[0];
        setValue(languageOption);
        break;
    }
  }, [id, dropdownOptions, locale]);

  // Language dropdown needs to keep the user on the same page, so we add the asPath to the linkUrl
  const dropdownOptionsWithPath = dropdownOptions.map((option: any) =>
    id === ContentfulDropdownIds.language
      ? {
          ...option,
          fields: {
            ...option.fields,
            linkUrl: `${option.fields.linkUrl}${pathname}`,
          },
        }
      : { ...option },
  );

  return (
    <div className="w-full">
      <h4
        className={classNames(
          'mb-2 whitespace-normal text-base capitalize tracking-normal',
          {
            'font-bold': boldTitle,
          },
        )}
      >
        {label}
      </h4>

      <div className="relative h-12 w-full" ref={dropdownRef}>
        <button className="relative w-full" onClick={() => setIsOpen(!isOpen)}>
          <div className="flex h-12 w-full items-center gap-1.5 border border-grey bg-white px-2 text-sm">
            {value?.fields?.linkImage && (
              <figure className="inline-block">
                <LuvImage
                  imageData={value.fields.linkImage}
                  width={27}
                  height={27}
                />
              </figure>
            )}
            {value?.fields?.linkText}
          </div>
          <ChevronIcon
            classes="absolute top-0 mt-4 right-2"
            direction={isOpen ? 'up' : 'down'}
          />
        </button>
        {isOpen ? (
          <ul className="absolute z-10 w-full bg-white shadow">
            {dropdownOptionsWithPath.map((option) => {
              return (
                <InputOption
                  key={option.sys.id}
                  option={option}
                  label={label}
                  className="border-b border-grey"
                />
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default InputDropDown;
